import { useEffect, useState } from "react";
import Paper from "@material-ui/core/Paper";
import { makeStyles, Typography } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import Alert from "@material-ui/lab/Alert";
import moment from "moment";
import CircularProgress from "@material-ui/core/CircularProgress";
import Button from "@material-ui/core/Button";
import { FormattedMessage, useIntl } from "react-intl";
import { DatePicker } from "@material-ui/pickers";

import { NewAnnouncement } from "./modals/NewAnnouncement";
import { AnnouncementItem } from "./components/AnnouncementItem";

import { BranchManagerService } from "../../../../services/BranchManagerService";
import Paginator, { DataTypeEnum } from "../../../../models/Paginator";

import TableData from "./components/TableData";

interface AnnouncementTabProps {
  branch: number;
}

const useStyles = makeStyles(() => ({
  main: {
    minHeight: "75vh",
    overflow: "hidden",
    width: "100%",
    flexGrow: 1,
    paddingLeft: 30,
    paddingRight: 30,
    paddingTop: 20,
    paddingBottom: 20,
  },
  newButton: {
    textTransform: "none",
    fontSize: 13,
  },
  dateTitle: {
    marginRight: 10,
    marginLeft: 20,
    marginTop: 5,
  }
}));

export const AnnouncementTab = (props: AnnouncementTabProps) => {
  const classes = useStyles();
  const { branch } = props;
  const [currentDateFrom, setCurrentDateFrom] = useState(moment());
  const [currentDateTo, setCurrentDateTo] = useState(moment());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [announceModalOpened, setAnnounceModalOpened] = useState(false);
  const [paginator, setPaginator] = useState(
    (): Paginator => Paginator.empty()
  );
  const intl = useIntl();

  const fetchAnnouncements = (branchId: number, page: number = 1) => {
    BranchManagerService.branchAnnouncements(branchId, page).then(
      (response) => {
        if (response.isSuccess) {
          switch (response.data.code) {
            case 100:
              let paginator = Paginator.fromJson(
                JSON.parse(JSON.stringify(response.data.data)),
                DataTypeEnum.announcements
              );
              setPaginator(paginator);
              break;
            case 110:
              setError(true);
              break;
          }
        } else {
          setError(true);
        }
        setLoading(false);
      }
    );
  };

  const paginatorAction = (page: number) => {
    fetchAnnouncements(branch, page);
  };

  useEffect(() => {
    setLoading(true);
    fetchAnnouncements(branch);
  }, []);

  const handleItemChange = () => {
    fetchAnnouncements(branch, paginator.page);
  };

  const toggleAnnounceModal = () => {
    setAnnounceModalOpened(!announceModalOpened);
    fetchAnnouncements(branch);
  };

  return (
    <>
      {announceModalOpened && (
        <NewAnnouncement
          closeCallback={toggleAnnounceModal}
          isOpened={announceModalOpened}
          branchId={branch}
        />
      )}

      <Paper className={classes.main} elevation={3}>
        <Grid container spacing={1}>
          <Grid xs={12} sm={12} lg={12}>
            {loading && <CircularProgress />}
            {error && (
              <Alert
                severity="error"
                action={
                  <Button
                    //onClick={() => setCurrentDate(moment())}
                    color="inherit"
                    size="small"
                  >
                    <FormattedMessage id="sharedScreen.loadAgain" />
                  </Button>
                }
              >
                <FormattedMessage id="sharedScreen.thereWasAProblemLoadingDatas" />
              </Alert>
            )}
          </Grid>
          <Grid xs={12} sm={12} lg={12} container justify="flex-end">
            <Grid container alignItems="flex-start" item xs={6} lg={6}>
              <Button
                color="secondary"
                variant="contained"
                className={classes.newButton}
                onClick={toggleAnnounceModal}
              >
                <FormattedMessage id="sharedScreen.newAnnouncement" />
              </Button>
            </Grid>
            <Grid
              container
              justify="flex-end"
              alignContent="flex-end"
              item
              xs={6}
              lg={6}
            >
              <Typography className={classes.dateTitle}>
                  <FormattedMessage id="sharedScreen.FROM" />
              </Typography>
              <DatePicker
                views={["year", "month", "date"]}
                maxDate={new Date()}
                value={currentDateFrom}
                onChange={setCurrentDateFrom}
              />
              
              <Typography className={classes.dateTitle}>
                  <FormattedMessage id="sharedScreen.TO" />
              </Typography>
              <DatePicker
                views={["year", "month", "date"]}
                maxDate={new Date()}
                value={currentDateTo}
                onChange={setCurrentDateTo}
              />
            </Grid>
          </Grid>
          <Grid item container xs={12} sm={12} lg={12}>
            <AnnouncementItem
              announcements={paginator.items}
              branch={branch}
              onItemChange={handleItemChange}
            />

            <TableData
              paginator={
                paginator.total !== 0
                  ? {
                      total: paginator.total,
                      page: paginator.page,
                      totalPage: paginator.total_page,
                      title: intl.formatMessage(
                        { id: "sharedScreen.totalAnnouncement" },
                        { total: paginator.total }
                      ),
                      handlePrevAction:
                        paginator.prev_page !== null
                          ? () => paginatorAction(paginator.page - 1)
                          : null,
                      handleNextAction:
                        paginator.next_page !== null
                          ? () => paginatorAction(paginator.page + 1)
                          : null,
                    }
                  : null
              }
            />
          </Grid>
          <br />
          <br />
          <br />
        </Grid>
      </Paper>
    </>
  );
};

export default AnnouncementTab;
